/**
 * Gift options (snippets/gift-options.liquid and the cart drawer editor in snippets/cart-drawer.liquid).
 *
 *  - Product page: live character count, and the message field only carries a `name` while it has
 *    text, so an empty message never becomes an empty property (which would split the cart line).
 *  - Cart drawer: "Add a gift message" / "Edit gift options" per item. Saving sends the line's
 *    properties to /cart/change.js (keeping any other properties, e.g. hidden "_" ones) and
 *    re-renders the drawer.
 *
 * The decision logic is pure so it can be tested with node (tests/gift-options.test.mjs).
 */
(function (root) {
  'use strict';

  /** Text for the counter, e.g. "32/200". Counts characters the way maxlength does (UTF-16 units). */
  function countText(value, max) {
    var n = typeof value === 'string' ? value.length : 0;
    return n + '/' + max;
  }

  /** A message is only sent when it has something other than whitespace. */
  function hasMessage(value) {
    return typeof value === 'string' && value.trim() !== '';
  }

  /**
   * Liquid's `item.properties | json` gives an array of [key, value] pairs, the Ajax API an object.
   * Accept either; anything else is treated as no properties.
   */
  function toObject(props) {
    var out = {};
    if (Array.isArray(props)) {
      props.forEach(function (pair) {
        if (Array.isArray(pair) && typeof pair[0] === 'string') out[pair[0]] = pair[1];
      });
      return out;
    }
    if (props && typeof props === 'object') {
      Object.keys(props).forEach(function (k) {
        out[k] = props[k];
      });
    }
    return out;
  }

  /**
   * New properties for a cart line: keeps every existing property, sets or removes the message
   * and the ship-to-her flag. Returns a new object; never mutates `existing`.
   */
  function buildProperties(existing, keys, message, shipToHer, shipValue) {
    var out = {};
    var src = toObject(existing);
    Object.keys(src).forEach(function (k) {
      if (k !== keys.message && k !== keys.ship && src[k] !== null && src[k] !== '') out[k] = src[k];
    });
    if (hasMessage(message)) out[keys.message] = message.trim();
    if (shipToHer) out[keys.ship] = shipValue;
    return out;
  }

  var api = { countText: countText, hasMessage: hasMessage, toObject: toObject, buildProperties: buildProperties };

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
    return;
  }
  root.DunstenGiftOptions = api;

  function wireMessage(textarea, counter) {
    var name = textarea.getAttribute('name');
    var max = parseInt(counter && counter.dataset.max, 10) || textarea.maxLength;
    var sync = function () {
      if (counter) counter.textContent = countText(textarea.value, max);
      if (hasMessage(textarea.value)) textarea.setAttribute('name', name);
      else textarea.removeAttribute('name');
    };
    textarea.addEventListener('input', sync);
    sync();
  }

  /** Swap in the drawer and cart bubble HTML returned with a cart change (same selectors as Dawn's CartDrawerItems). */
  function renderDrawer(sections) {
    if (!sections || !sections['cart-drawer']) {
      var items = document.querySelector('cart-drawer-items');
      if (items && typeof items.onCartUpdate === 'function') items.onCartUpdate();
      return;
    }
    var parse = function (html, selector) {
      var el = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
      return el ? el.innerHTML : null;
    };
    var inner = document.querySelector('#CartDrawer .drawer__inner');
    var drawerHtml = parse(sections['cart-drawer'], '.drawer__inner');
    if (inner && drawerHtml !== null) inner.innerHTML = drawerHtml;
    var bubble = document.getElementById('cart-icon-bubble');
    var bubbleHtml = sections['cart-icon-bubble'] ? parse(sections['cart-icon-bubble'], '.shopify-section') : null;
    if (bubble && bubbleHtml !== null) bubble.innerHTML = bubbleHtml;
  }

  /** After a re-render, put keyboard focus back on the edited item's link (matched by its new properties). */
  function focusEditor(properties) {
    var want = JSON.stringify(properties);
    var editors = document.querySelectorAll('#CartDrawer cart-gift-editor');
    for (var i = 0; i < editors.length; i++) {
      var props = {};
      try {
        props = toObject(JSON.parse(editors[i].dataset.properties || '{}'));
      } catch (e) {}
      if (JSON.stringify(props) === want) {
        var summary = editors[i].querySelector('summary');
        if (summary) summary.focus();
        return;
      }
    }
  }

  if (!customElements.get('gift-options')) {
    customElements.define(
      'gift-options',
      class GiftOptions extends HTMLElement {
        connectedCallback() {
          if (this.wired) return;
          this.wired = true;
          wireMessage(this.querySelector('[data-gift-message]'), this.querySelector('[data-gift-count]'));
        }
      }
    );
  }

  if (!customElements.get('cart-gift-editor')) {
    customElements.define(
      'cart-gift-editor',
      class CartGiftEditor extends HTMLElement {
        connectedCallback() {
          if (this.wired) return;
          this.wired = true;
          // The drawer's <cart-drawer-items> treats every 'change' event as a quantity edit; keep ours to ourselves.
          this.addEventListener('change', function (e) { e.stopPropagation(); });
          this.textarea = this.querySelector('[data-gift-message]');
          this.checkbox = this.querySelector('[data-gift-ship]');
          this.status = this.querySelector('[data-gift-status]');
          var counter = this.querySelector('[data-gift-count]');
          var max = parseInt(counter && counter.dataset.max, 10) || 200;
          var update = function () { if (counter) counter.textContent = countText(this.textarea.value, max); }.bind(this);
          this.textarea.addEventListener('input', update);
          update();
          this.querySelector('[data-gift-save]').addEventListener('click', this.save.bind(this));
        }

        save() {
          var existing = {};
          try {
            existing = toObject(JSON.parse(this.dataset.properties || '{}'));
          } catch (e) {
            existing = {};
          }
          var keys = { message: this.dataset.messageKey, ship: this.dataset.shipKey };
          var properties = buildProperties(existing, keys, this.textarea.value, this.checkbox.checked, this.dataset.shipValue);
          var button = this.querySelector('[data-gift-save]');
          if (this.saving) return;
          if (JSON.stringify(properties) === JSON.stringify(existing)) {
            this.querySelector('details').open = false;
            return;
          }
          this.saving = true;
          button.disabled = true;
          this.status.textContent = '';
          // Address the line by its key, not its position: a stale position could hit another product.
          var key = this.dataset.key;
          var quantity = parseInt(this.dataset.quantity, 10);
          // Ask for the re-rendered drawer in the same request, as Dawn's quantity buttons do: a separate
          // GET of ?section_id=cart-drawer can come back stale right after a cart change.
          var sections = ['cart-drawer', 'cart-icon-bubble'];
          var post = function (url, body) {
            body.sections = sections;
            body.sections_url = window.location.pathname;
            return fetch(url, { ...fetchConfig(), body: JSON.stringify(body) }).then(function (r) {
              if (!r.ok) throw new Error(url + ' ' + r.status);
              return r.json();
            });
          };
          var request;
          if (Object.keys(properties).length > 0) {
            request = post(window.routes.cart_change_url, { id: key, quantity: quantity, properties: properties });
          } else {
            // Shopify ignores an empty `properties` object, so clearing the last property means
            // removing the line and adding the same variant (and selling plan) back without any.
            var item = { id: parseInt(this.dataset.variantId, 10), quantity: quantity };
            if (this.dataset.sellingPlan) item.selling_plan = parseInt(this.dataset.sellingPlan, 10);
            request = post(window.routes.cart_change_url, { id: key, quantity: 0 }).then(function () {
              return post(window.routes.cart_add_url + '.js', { items: [item] });
            });
          }
          request
            .then(function (response) {
              renderDrawer(response && response.sections);
              focusEditor(properties);
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: response });
            })
            .catch(function (e) {
              console.error('Gift options: could not save', e);
              this.saving = false;
              button.disabled = false;
              this.status.textContent = window.cartStrings ? window.cartStrings.error : 'Something went wrong. Please try again.';
              // Show the real cart: a failed re-add after the remove would otherwise leave a stale drawer.
              var items = document.querySelector('cart-drawer-items');
              if (items && typeof items.onCartUpdate === 'function') items.onCartUpdate();
            }.bind(this));
        }
      }
    );
  }
})(typeof window !== 'undefined' ? window : this);
